<template>
  <div
      class="mode-picker-cont"
  >
    <div
        class="theme-mode"
        :class="$vuetify.theme.dark ? 'theme_dark active' : 'theme_light'"
        @click="selectThemeMode(true)"
    >
      <div
          class="theme-mode__title"
          :class="$vuetify.theme.dark ? 'theme_dark' : 'theme_light'"
      >
        Dark
      </div>
      <div class="theme-mode__icon">
        <v-icon
            x-large
        >
          far fa-moon
        </v-icon>
      </div>
    </div>
    <div
        class="theme-mode"
        :class="$vuetify.theme.dark ? 'theme_dark' : 'theme_light active'"
        @click="selectThemeMode(false)"
    >
      <div
          class="theme-mode__title"
          :class="$vuetify.theme.dark ? 'theme_dark' : 'theme_light'"
      >
        Light
      </div>
      <div class="theme-mode__icon">
        <v-icon
            x-large
        >
          far fa-sun
        </v-icon>
      </div>
    </div>
  </div>
</template>

<script>
import {APP_STATE_SECTIONS} from "@/utilities/enums";
import {mapGetters, mapMutations} from "vuex";
import {SettingsService} from "@/services/ApiService";
import {cloneDeep} from "lodash";

/**
 * @component ModePicker
 * a UI component that enables theme mode selection between dark and light mode
 * for the application and updates the current user's settings
 */
export default {
  name: 'ModePicker',
  computed: {
    ...mapGetters(["getSettings"]),
  },
  methods: {
    ...mapMutations(["updateApplicationState"]),
    /**
     * Select the theme mode based on the received argument as
     * passed by the theme mode icon component buttons
     *
     * @param dark {boolean} - specifies if the selected theme mode is 'dark'
     * @returns {Promise<void>}
     */
    async selectThemeMode(dark) {
      // console.log(`dark: ${dark}`);
      try {
        let elements = document.getElementsByName("theme-color");
        if (elements.length) {
          elements[0].content = dark ? "#15151a" : "#edeef2";
        }
        const updateOptions = {
          section: APP_STATE_SECTIONS.DARK,
          mutation: dark,
        };
        this.updateApplicationState(updateOptions);
        let updatedSettings = cloneDeep(this.getSettings);
        this.updateAppSettingsRemotely({settings: updatedSettings});
        this.$vuetify.theme.dark = dark;
      } catch (error) {
        console.error(error);
      }
    },
    /**
     * Update the settings for the current account on the database
     *
     * @param settings {Object} - the settings data to update
     * @param recursive {Boolean}
     */
    updateAppSettingsRemotely({settings, recursive}) {
      let profileId = parseInt(
          localStorage.getItem("currentSettingsProfileId"),
          10
      );

      if (!profileId && profileId !== 0) {
        if (this.isAdmin)
          console.log(
              "No profile ID found when updating the application state."
          );
        return;
      }

      let requestStatus = "200";

      SettingsService.put(profileId, settings)
          .then(() => {
          })
          .catch((error) => {
            if (
                error.hasOwnProperty("message") &&
                error.message.search("404") !== -1 &&
                !recursive
            ) {
              requestStatus = "404";
              // Get the configuration for the current type of device
              return SettingsService.getMe({
                params: {
                  device:
                      localStorage.getItem("selected-device-type") || "Desktop",
                },
              });
              // return SettingsService.getMe({ params: { device: "Desktop" } })
            }
          })
          .then((response) => {
            if (requestStatus === "404" && response.data && !recursive) {
              localStorage.setItem(
                  "currentSettingsProfileId",
                  response.data.id
              );

              this.updateAppSettingsRemotely({
                settings: settings,
                recursive: true,
              });
            }
          })
          .catch((error) => {
            if (
                error.hasOwnProperty("message") &&
                error.message.search("404") !== -1
            ) {
              this.postNewSetting(false, settings);
            }
          });
    },
  }
}
</script>

<style lang="scss" scoped>
.mode-picker-cont {
  display: flex;
  gap: 24px;

  .theme-mode {
    padding: 12px;
    border: 1px solid;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color ease-in-out 0.1s;

    &.theme_dark {
      &:hover {
        background-color: rgba(250, 250, 250, 0.2);
      }

      &.active {
        background-color: rgba(250, 250, 250, 0.1);
        border-color: #8260b9 !important;
        border-width: 2px;
        box-shadow: 0 0 3px rgb(0, 0, 0);
      }
    }

    &.theme_light {
      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
      }

      &.active {
        background-color: rgba(0, 0, 0, 0.15);
        border-color: #8260b9 !important;
        border-width: 2px;
        box-shadow: 0 0 3px rgb(0, 0, 0);
      }
    }

    &__title {
      text-align: center;
      text-transform: uppercase;
      font-weight: 600;
      margin-bottom: 12px;
    }

    &__icon {
      margin: 24px;
    }
  }
}

.theme_dark {
  color: #47bd96 !important;
  border-color: #47bd96 !important;
}

.theme_light {
  color: #1c6e59 !important;
  border-color: #1c6e59 !important;
}
</style>